import { useState } from 'react'

import { GetServerSideProps, NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { useErrorBoundary } from 'react-error-boundary'

import {
  restaurantGetRestaurant,
  restaurantNewRestaurantPhotoClip,
  restaurantNewRestaurantPhotoClipImage,
} from '~/api-client/food_api/restaurant'
import { Image, Restaurant } from '~/api-definition/definition'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { Loading } from '~/components/data-display/Loading'
import { SelectV2 } from '~/components/data-input/SelectV2'
import { AddImage } from '~/components/food_app/image/AddImage'
import { PhotoClipDatePicker } from '~/components/food_app/photo_clip/PhotoClipDatePicker'
import { ChevronLeftIcon, CloseIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { convertDateYYYYMMDD } from '~/libs/date'
import { preprocessImages } from '~/libs/image'
import { useSession } from '~/provider/SessionProvider'

type Props = {
  restaurant: Restaurant
}

const Page: NextPage<Props> = (props) => {
  const router = useRouter()
  const { id } = router.query as { id: string }
  const session = useSession({ loginRequired: true })
  const accessToken = session.response?.data?.accessToken
  const [mode, setMode] = useState<'new' | 'edit' | null>('new')
  const [visitedDate, setVisistedDate] = useState<Date | null>()
  const [openModal, setOpenModal] = useState(false)
  const [uploadedImages, setUploadedImages] = useState<Image[]>([])
  const [uploading, setUploading] = useState(false)
  const { showBoundary } = useErrorBoundary()

  const onInputImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files == null) {
      return
    }
    if (e.currentTarget.files.length === 0) {
      return
    }
    if (accessToken == null) {
      return
    }
    setUploading(true)
    try {
      const pImages = await preprocessImages(
        Array.from(e.currentTarget.files),
        'restaurant_photo_clip'
      )
      if (pImages == null) {
        return
      }
      const responses = await Promise.all(
        pImages.map(async (file) => {
          return restaurantNewRestaurantPhotoClipImage({
            accessToken: accessToken,
            body: {
              restaurant_id: id,
              image_file: file,
            },
          })
        })
      )
      const images = responses.map((r) => r.image)
      setUploadedImages(uploadedImages.concat(images))
    } catch (e) {
      showBoundary(e)
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (accessToken == null) {
      return
    }
    try {
      const formData = new FormData(e.currentTarget)
      const privacyType = formData.get('privacyType')
      await restaurantNewRestaurantPhotoClip({
        accessToken: accessToken,
        body: {
          restaurant_id: id,
          privacy_type:
            privacyType === 'public' || privacyType === 'private'
              ? privacyType
              : 'public',
          image_ids: uploadedImages.map((i) => i.imageId),
          visited_date: visitedDate
            ? convertDateYYYYMMDD(visitedDate)
            : undefined,
        },
      })
      setOpenModal(true)
    } catch (e) {
      showBoundary(e)
    }
  }

  return (
    <>
      <Head>
        <title>{props.restaurant.restaurantName}</title>
      </Head>
      <Header>
        <ChevronLeftIcon fill="#fff" onClick={() => router.back()} />
        <div />
        {props.restaurant.restaurantName}
        <div />
        <div />
      </Header>

      {<Loading loading={uploading} loadingText={'Uploading'} />}

      <Container>
        <ContainerInset>
          <form className="max-w-[500px] mx-auto" onSubmit={handleSubmit}>
            <div className="flex text-sm mb-4">
              追加する写真を選んでください。
            </div>

            <div className="grid grid-cols-3 gap-2">
              {uploadedImages.map((image, i) => {
                return (
                  <div
                    key={i}
                    className="w-[110px] h-[110px] overflow-hidden mx-auto relative"
                  >
                    <button
                      type="button"
                      className="outline-none absolute top-0 right-0 pr-2 pt-2"
                    >
                      <CloseIcon
                        onClick={() => {
                          const newImages = uploadedImages.filter(
                            (i2) => i2.imageId !== image.imageId
                          )
                          setUploadedImages(newImages)
                        }}
                        className="fill-white w-[14px] h-[14px]"
                      />
                    </button>
                    <img
                      alt={`投稿画像${i + 1}`}
                      className="w-[110px] h-[110px] object-cover rounded"
                      src={image.imageUrl}
                    />
                  </div>
                )
              })}
              <label>
                <AddImage className="w-[110px] h-[110px]" />
                <input
                  type="file"
                  accept="image/*,.heic,.heif,.dng"
                  multiple
                  className="hidden"
                  onInput={onInputImageFile}
                />
              </label>
            </div>

            {(mode === 'new' || mode === 'edit') && (
              <>
                <PhotoClipDatePicker
                  visitedDate={visitedDate}
                  setVisitedDate={function (date: Date | null): void {
                    setVisistedDate(date)
                  }}
                  containerProps={{ className: 'mb-4' }}
                />

                <SelectV2
                  name="privacyType"
                  label="表示範囲"
                  defaultValue="public"
                  required
                  options={[
                    { label: '全体公開', value: 'public' },
                    { label: '自分のみ', value: 'private' },
                  ]}
                />

                <div className="flex justify-between mt-8">
                  <Button
                    variant="functionWeak"
                    fontSize="sm"
                    onClick={() => router.push(`/restaurants/${id}/images`)}
                  >
                    一覧に戻る
                  </Button>
                  <Button variant="primary" fontSize="sm" type="submit">
                    {mode === 'new' ? '写真を投稿する' : '更新する'}
                  </Button>
                </div>
              </>
            )}

            <Modal
              isOpen={openModal}
              setIsOpen={setOpenModal}
              title={''}
              body={
                <div>
                  <div className="text-center text-sm font-bold flex items-center justify-center mb-4">
                    <img src="/images/star1.png" alt="star1" />
                    <p className="mx-2">写真投稿ありがとうございます！</p>
                    <img src="/images/star2.png" alt="star2" />
                  </div>
                  <img
                    src={uploadedImages[0]?.imageUrl}
                    alt="thanks"
                    className="w-full"
                  />
                  <div className="text-center mt-4">
                    <p>あなたの投稿がフーディーたちの</p>
                    <p>お店選びの参考になります。</p>
                  </div>
                </div>
              }
              actionButtons={
                <div className="mt-8">
                  <Button
                    type="button"
                    className="bg-gray w-full"
                    variant="functionWeak"
                    shrink
                    onClick={async () => {
                      setOpenModal(false)
                      router.push(`/restaurants/${id}`)
                    }}
                  >
                    OK
                  </Button>
                </div>
              }
            />
          </form>
        </ContainerInset>
      </Container>
      <Footer />
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const restaurantId = context.query.id as string
  const restaurantResponse = await restaurantGetRestaurant({
    searchParams: { restaurant_id: restaurantId },
  })
  const props = {
    restaurant: restaurantResponse.restaurant,
  }
  return { props }
}

export default Page
